import { Box, Card, CircularProgress, Divider, TextField, InputAdornment } from "@mui/material";
import React, { useEffect, useState } from "react";
import Table from "../components/Table";
import { getUserScanHistory, getScanPdf, getUpdatedScanPdf } from "../utils/api";
import { BsFileEarmarkPdfFill } from "react-icons/bs";
import SearchIcon from "@mui/icons-material/Search";
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';

const ACCENT_BLUE = '#22638f';
const ACCENT_ORANGE = '#d96c3c';
const TEXT_MUTED = '#93a0b5';
const TEXT_BODY = '#3b4a63';
const BORDER = '#eef2f8';

const cardShell = {
    borderRadius: '16px',
    boxShadow: '0 2px 10px rgba(12, 61, 110, 0.05)',
    border: `1px solid ${BORDER}`,
};

function ScanHistroy() {
    const [scansList, setScanList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingPdf, setLoadingPdf] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [windowSize, setWindowSize] = useState(window.innerWidth);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const [rowCount, setRowCount] = useState(0);

    useEffect(() => {
        const resizeFn = () => setWindowSize(window.innerWidth);
        window.addEventListener("resize", resizeFn);
        return () => window.removeEventListener("resize", resizeFn);
    }, []);

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);

                const id = localStorage.getItem("user_id");

                const limit = paginationModel.pageSize;
                const offset = paginationModel.page * paginationModel.pageSize;
                const response = await getUserScanHistory(id, limit, offset);

                if (response?.data) {
                    const dataList =
                        response.data.scan_data?.map((item, index) => ({
                            ...item,
                            id: offset + index + 1,
                            scan_date: item.scan_date,
                        })) || [];

                    setScanList(dataList);
                    setRowCount(response.data?.total_count);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        })();
    }, [paginationModel]);

    const downoadPdf = async (data) => {
        try {
            setLoadingPdf(true);
            if (data.has_updated_result) {
                await getUpdatedScanPdf(data.scan_id);
            } else {
                await getScanPdf(data.scan_id);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingPdf(false);
        }
    };

    const columns = [
        {
            field: "id",
            headerName: "S No.",
            width: windowSize < 768 ? 90 : 90,
            headerClassName: 'table-header',
            editable: false,
        },
        {
            field: "scan_id",
            headerName: "Scan ID",
            width: windowSize < 768 ? 150 : 200,
            headerClassName: 'table-header',
            editable: false,
        },
        {
            field: "scan_date",
            headerName: "Scan Date",
            width: windowSize < 768 ? 170 : 200,
            headerClassName: 'table-header',
        },
        {
            field: "crop_name",
            headerName: "Crop",
            width: windowSize < 768 ? 150 : 120,
            headerClassName: 'table-header',
            editable: false,
        },
        {
            field: "variety_name",
            headerName: "Variety",
            width: windowSize < 768 ? 150 : 230,
            headerClassName: 'table-header',
            editable: false,
        },
        {
            field: "machine_name",
            headerName: "Machine",
            width: windowSize < 768 ? 150 : 150,
            headerClassName: 'table-header',
            editable: false,
        },
        {
            field: "action",
            headerName: "PDF",
            flex: 1,
            minWidth: 100,
            sortable: false,
            filterable: false,
            headerClassName: 'table-header',
            renderCell: (rowData) => (
                <BsFileEarmarkPdfFill
                    className="cursor"
                    style={{ marginLeft: 10, fontSize: 20, color: ACCENT_BLUE }}
                    onClick={() => downoadPdf(rowData.row)}
                />
            ),
        },
    ];

    return (
        <Box sx={{ p: { xs: 2, md: 0}, mt: 5, position: 'relative' }}>
            {loadingPdf && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255,255,255,0.6)',
                        zIndex: 10,
                        borderRadius: '16px',
                    }}
                >
                    <CircularProgress size={28} sx={{ color: ACCENT_BLUE }} />
                </Box>
            )}

            {!loading ? (
                scansList.length ? (
                    <Card sx={{ ...cardShell, backgroundColor: '#fff', overflow: 'hidden' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 2,
                                px: 2.5,
                                py: 2,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Box
                                    sx={{
                                        width: 34, height: 34, borderRadius: '10px',
                                        backgroundColor: `${ACCENT_ORANGE}1A`, color: ACCENT_ORANGE,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}
                                >
                                    <ManageHistoryIcon sx={{ fontSize: 19 }} />
                                </Box>
                                <p style={{ fontSize: 15, fontWeight: 700, color: TEXT_BODY, margin: 0 }}>
                                    Scan History List
                                </p>
                            </Box>

                            <TextField
                                placeholder="Search"
                                size="small"
                                onChange={(e) => setSearchTerm(e.target.value)}
                                sx={{
                                    width: 240,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '10px',
                                        backgroundColor: '#fff',
                                        height: 40,
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT_BLUE },
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ fontSize: 18, color: TEXT_MUTED }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        <Divider />

                        <Table
                            rows={scansList}
                            columns={columns}
                            loading={loading}
                            rowCount={rowCount}
                            page={paginationModel.page}
                            pageSize={paginationModel.pageSize}
                            pageSizeOptions={[10, 25, 50, 100]}
                            onPageChange={(page, pageSize) => setPaginationModel({ page, pageSize })}
                            searchQuery={searchTerm}
                        />
                    </Card>
                ) : null
            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress sx={{ color: ACCENT_BLUE }} />
                </Box>
            )}
        </Box>
    );
}

export default ScanHistroy;